import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { Skeleton } from '@mantine/core';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Button from '@/components/Button';
import FullScreenGallery from '@/components/FullScreenGallery';
import ProductImages from '@/components/ProductImages';

import styles from './styles.module.scss';
import { ProductGalleryProps } from './types';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const ProductGallery = ({
  images,
  title,
  thumbnail,
  isMobile,
  status,
  isOnSale,
  isClassic,
  onOpenChange = () => {},
}: ProductGalleryProps) => {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [productImagesSwiperRef, setProductImagesSwiperRef] =
    useState<SwiperType | null>(null);
  const [mainPhotoSwiperRef, setMainPhotoSwiperRef] =
    useState<SwiperType | null>(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => setIsClient(true), []);

  const openModal = () => setOpen(true);

  const galleryPhotos = useMemo(() => {
    const mainPhoto = thumbnail || images[0]?.url;
    if (!images) return [];
    return [{ id: mainPhoto, url: mainPhoto }, ...images];
  }, [images, thumbnail]);

  useEffect(() => {
    onOpenChange(open);
  }, [open]);

  return (
    <>
      {!isClient && (
        <Skeleton
          width={600}
          height={600}
          radius="md"
          style={{ margin: '0 auto' }}
        />
      )}
      {isClient && (
        <>
          <FullScreenGallery
            isOpen={open}
            onClick={() => setOpen(false)}
            mainPhoto={thumbnail || images[0]?.url}
            currentIndex={currentIndex}
            images={images}
            title={title}
            thumbnail={thumbnail}
            isMobile={isMobile}
          />
          <div className={styles.photoWrapper}>
            {status !== 'published' && (
              <div className={styles.soldStatus}>Produkt wyprzedany</div>
            )}
            <div className={styles.badgesWrapper}>
              {isOnSale && status === 'published' && (
                <div className={styles.saleBadge}>Sale</div>
              )}
              {isClassic && status === 'published' && (
                <div className={styles.classicBadge}>Klasyk</div>
              )}
            </div>
            <div className={styles.mainPhotoWrapper}>
              <Button
                className={`${styles.navigationButton} main-photo-button-prev`}
                variant="icon"
              >
                <ChevronLeftIcon />
              </Button>
              <Swiper
                modules={[Navigation, Pagination]}
                navigation={{
                  nextEl: '.main-photo-button-next',
                  prevEl: '.main-photo-button-prev',
                }}
                pagination={isMobile ? { clickable: true } : false}
                slidesPerView={1}
                spaceBetween={10}
                autoHeight
                onSwiper={setMainPhotoSwiperRef}
                onSlideChange={(swiper: SwiperType) => {
                  if (productImagesSwiperRef) {
                    productImagesSwiperRef.slideTo(swiper.activeIndex);
                    setCurrentIndex(swiper.activeIndex);
                  }
                }}
              >
                {galleryPhotos.map((image, index) => (
                  <SwiperSlide key={index}>
                    <Image
                      src={image.url}
                      alt={`Zdjęcie produktu ${title}`}
                      layout="responsive"
                      width={600}
                      height={800}
                      style={{ objectFit: 'contain', cursor: 'pointer' }}
                      priority
                      onClick={openModal}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              <Button
                className={`${styles.navigationButton} main-photo-button-next`}
                variant="icon"
              >
                <ChevronRightIcon />
              </Button>
            </div>
          </div>
          {images.length > 0 && !isMobile && (
            <ProductImages
              images={images}
              title={title}
              thumbnail={thumbnail}
              parentSwiperRef={mainPhotoSwiperRef}
              setSwiperRef={setProductImagesSwiperRef}
            />
          )}
        </>
      )}
    </>
  );
};

export default ProductGallery;
